// ============================================================
// View: Spielarten — pro Spielart KPI-Karten + Vergleichstabellen
// ============================================================

import { useMemo } from 'react';
import { Card, CardTitle, EurCell, NetCell } from '../components/ui';
import { DivergingHBars, HBarChart } from '../components/charts';
import { artColor, CO } from '../theme';
import { useApp } from '../store';
import { artRows, inRange, period } from '../aggregator';
import { eur, fmtNum, pct, sEur } from '../format';

export function Spielarten() {
  const { history, periodMode, customFrom, customTo } = useApp();
  const work = history?.eintraege ?? [];
  const p = useMemo(() => period(periodMode, work, customFrom, customTo), [periodMode, work, customFrom, customTo]);
  const filtered = useMemo(() => (p.full ? work.slice() : work.filter((e) => inRange(e, p))), [work, p]);
  const arts = useMemo(() => artRows(filtered), [filtered]);

  if (!arts.length) return null;
  const totalEinsatz = arts.reduce((s, a) => s + a.einsatz, 0);

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(arts.length, 5)}, 1fr)`,
          gap: 16,
          marginBottom: 20,
        }}
      >
        {arts.slice(0, 5).map((a) => (
          <div
            key={a.spielart}
            style={{
              background: CO.card,
              border: `1px solid ${CO.border}`,
              borderTop: `3px solid ${artColor(a.spielart)}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, minHeight: 34 }}>{a.spielart}</div>
            <div style={{ fontSize: 11.5, color: CO.faint }}>Nettoergebnis</div>
            <div
              className="num"
              style={{ fontSize: 20, fontWeight: 700, color: a.netto >= 0 ? CO.pos : CO.neg }}
            >
              {sEur(a.netto)}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
                fontSize: 11.5,
                color: CO.muted,
              }}
            >
              <span>{fmtNum(a.n)} Einträge</span>
              <span className="num">Rückfl. {pct(a.rueckfluss)}</span>
            </div>
          </div>
        ))}
      </div>

      <Card>
        <CardTitle
          title="Vergleichstabelle"
          sub={`${p.label} · alle Werte historisch, jeweils mit Stichprobengröße (Einträge)`}
        />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: CO.faint }}>
              {[
                'Spielart',
                'Einträge',
                'Einsatz',
                'Auszahlung',
                'Nettoergebnis',
                'Rückflussquote',
                'Auszahlungsquote',
                'Nettogewinnquote',
                'Ø Einsatz',
                'Höchste Ausz.',
              ].map((c, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    fontWeight: 500,
                    padding: '0 0 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {arts.map((a) => (
              <tr key={a.spielart} style={{ borderTop: `1px solid ${CO.border2}` }}>
                <td style={{ padding: '9px 8px 9px 0' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: artColor(a.spielart) }} />
                    {a.spielart}
                  </span>
                </td>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {fmtNum(a.n)}
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.einsatz} />
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.gewinn} tone="auszahlung" />
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <NetCell n={a.netto} />
                </td>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {pct(a.rueckfluss)}
                </td>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {pct(a.auszahlungsquote)}
                </td>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {pct(a.nettogewinnquote)}
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.avgEinsatz} />
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.maxG} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle title="Größter Anteil am Gesamteinsatz" sub={p.label} />
          <HBarChart
            rows={arts.map((a) => ({
              label: a.spielart,
              v: a.einsatz,
              pctv: totalEinsatz ? (a.einsatz / totalEinsatz) * 100 : 0,
            }))}
            colorFn={(r) => artColor(r.label)}
          />
        </Card>
        <Card>
          <CardTitle title="Historisches Nettoergebnis im gewählten Zeitraum" sub={p.label} />
          <DivergingHBars rows={arts.map((a) => ({ label: a.spielart, netto: a.netto }))} />
        </Card>
      </div>
    </div>
  );
}
